import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { employeeRolesTypes } from '@shared/helpers/types.helper';
import { IEmployee } from '@shared/interfaces/employee.interface';
import { IError } from '@shared/interfaces/error.interface';
import { Company } from '@shared/models/company.model';
import { AssetCategorysService } from '@shared/services/assets-categories.service';
import { CompaniesService } from '@shared/services/companies.service';
import { EmployeesService } from '@shared/services/employees.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { UsersService } from '@shared/services/users.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-modals-employees',
  templateUrl: './modals.employees.component.html'
})
export class ModalsEmployeesComponent
  extends ModalDirective<Partial<IEmployee>>
  implements OnInit {
  @Input() public openModal: Subject<Partial<IEmployee>>;
  public defaultCompanyValue: string;
  public defaultUserValue: string;

  public title = String('MODALS.EMPLOYEES.ADD.TITLE');
  public description = String('MODALS.EMPLOYEES.ADD.DESCRIPTION');
  public entry: FormGroup;
  public disableButtons = Boolean(false);
  public submitKey = String('BUTTONS.SUBMIT');
  public deleteKey: string;

  public employeeRoles: { value: number; label: string }[] = employeeRolesTypes;
  public errors: IError[] = [];

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    public readonly _companies: CompaniesService,
    public readonly _users: UsersService,
    private readonly _assetsCategory: AssetCategorysService,
    private readonly _logger: LoggerService,
    private readonly _fb: FormBuilder,
    private readonly _employees: EmployeesService,
    private readonly _toasts: ToastService
  ) {
    super();

    this.onGetValueFromAttributes = this.onGetValueFromAttributes.bind(this);
    this.onMatOptionSelect = this.onMatOptionSelect.bind(this);
  }

  ngOnInit(): void {
    this.createForm();

    this.openModal.subscribe((e: IEmployee) => {
      if (!!e) {
        this.defaultCompanyValue = e.company.name;
        this.defaultUserValue = e.user.fullName;

        this.title = 'MODALS.EMPLOYEES.EDIT.TITLE';
        this.description = 'MODALS.EMPLOYEES.EDIT.DESCRIPTION';
        this.deleteKey = 'BUTTONS.DELETE';

        this.entry.patchValue(e);
      } else {
        this.title = 'MODALS.EMPLOYEES.ADD.TITLE';
        this.description = 'MODALS.EMPLOYEES.ADD.DESCRIPTION';

        this.defaultCompanyValue = null;
        this.defaultUserValue = null;
        this.deleteKey = null;
      }
    });
  }

  public whenModalClose(type: string): void {
    if (type === 'SUBMIT') {
      this.errors = [];
      this.entityForm.ngSubmit.emit();
    } else if (type === 'DELETE') {
      this.delete();
    }
  }

  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      this.disableButtons = true;
      this.entry.disable();

      let method = 'post';
      let url = `${method.toUpperCase()} /employees`;
      if (value.id) {
        method = 'patch';
        url = `${method.toUpperCase()} /employees/${value.id}`;
      }

      this._employees[method](value).subscribe(
        (res: IEmployee) => {
          this._logger.info(this.constructorName, url, res);

          this.entry.enable();
          this.entry.reset();
          this.disableButtons = false;

          this.resetModal();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);

          const errors = err.errors;
          if (errors) {
            this.errors = errors;

            if (errors[0].detail) {
              this._toasts.error(errors[0].detail);
            }
          }

          this.disableButtons = false;
          this.entry.enable();
        }
      );
    }
  }

  public onGetValueFromAttributes(e: any) {
    if (e instanceof Company) {
      return e.name;
    } else {
      return `${e.fullName} ${e.email ? '(' + e.email + ')' : null}`;
    }
  }

  public onMatOptionSelect(e: any): void {
    let type = 'user';
    if (e instanceof Company) {
      type = 'company';
    }

    this.entry.get(type).patchValue({
      id: e.id
    });
  }

  protected createForm() {
    this.entry = this._fb.group({
      id: [''],
      role: ['', [Validators.required]],
      company: this._fb.group({
        id: ['', [Validators.required]]
      }),
      user: this._fb.group({
        id: ['']
      })
    });
  }

  private delete() {
    const id = this.entry.get('id').value;
    const url = `DELETE /employees/${id}`;

    this._employees.delete(id).subscribe(
      (res: IEmployee) => {
        this._logger.info(this.constructorName, url, res);

        this.entry.enable();
        this.resetModal();
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        const errors = err.errors;
        if (errors) {
          this.errors = errors;

          if (errors[0].detail) {
            this._toasts.error(errors[0].detail);
          }
        }

        this.entry.enable();
      }
    );
  }
}
